import { useCallback, useEffect, useRef, useState } from "react";
import {
  Mic,
  Loader2,
  Play,
  Pause,
  Sparkles,
  Trash2,
  UploadCloud,
  SlidersHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  VOICE_CATEGORY_BY_ID,
  VOICE_CATEGORY_ORDER,
  type VoiceCategoryKey,
} from "./constants";

// Voice clone CTAs are hidden while the ElevenLabs subscription is on a tier
// that does not include Instant Voice Cloning. All clone code (modal, hook,
// server endpoint, DB grant, gating) is intact — flip this to `true` once
// the subscription is upgraded and IVC is available again.
const SHOW_CLONE_CTAS = false;

export interface VoiceOption {
  id: string;
  nameKey: string;
  gender: "male" | "female";
  language: "en" | "he" | "multilingual";
  previewUrl: string;
  /** True for the user's own cloned voice (renders differently). */
  isCloned?: boolean;
}

interface TalkingAvatarVoicePickerProps {
  /** Display number — shifts with the chosen image mode path. */
  stepNumber: number;
  voices: VoiceOption[];
  isLoading: boolean;
  selectedVoiceId: string | null;
  onSelectVoice: (id: string) => void;
  /** True when the user is signed in (regardless of plan). Drives the
   *  upsell vs. login CTA copy. */
  isAuthenticated: boolean;
  /** Action lets the picker offer two entry points (record / upload) into
   *  the same clone modal. The picker decides which one the user clicked;
   *  the hook decides what to do (gate → PricingModal or open the modal
   *  with that initial action highlighted). */
  onOpenCloneModal: (action: "record" | "upload") => void;
  onDeleteClonedVoice: () => void;
}

export function TalkingAvatarVoicePicker({
  stepNumber,
  voices,
  isLoading,
  selectedVoiceId,
  onSelectVoice,
  isAuthenticated,
  onOpenCloneModal,
  onDeleteClonedVoice,
}: TalkingAvatarVoicePickerProps) {
  const { t, isRTL } = useLanguage();
  // One <audio> element is reused for all previews — playing a new card
  // automatically stops the previous one.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  // Category filter — a "Categories" toggle reveals 3 buttons; null = show all.
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<VoiceCategoryKey | null>(null);
  const categoryName = (c: VoiceCategoryKey) =>
    (t.talkingAvatar.voiceCategoryNames as Record<string, string>)[c];

  // Mobile horizontal-scroll affordance — a chevron that appears while more
  // cards remain off-screen and scrolls one page on tap (native swipe stays).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const updateCanScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    // Math.abs handles RTL (scrollLeft goes negative) and LTR uniformly.
    setCanScrollMore(Math.abs(el.scrollLeft) < maxScroll - 1);
  }, []);
  const scrollForward = () => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = el.clientWidth * 0.8;
    el.scrollBy({ left: isRTL ? -delta : delta, behavior: "smooth" });
  };

  const playPreview = (voice: VoiceOption) => {
    if (!voice.previewUrl) return;
    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(voice.previewUrl);
    audio.addEventListener("ended", () => setPlayingId(null));
    audio.addEventListener("pause", () => setPlayingId((id) => (id === voice.id ? null : id)));
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(voice.id);
  };

  const voiceName = (v: VoiceOption) =>
    v.isCloned ? v.nameKey : ((t.talkingAvatar.voices as Record<string, string>)[v.id] ?? v.id);
  // Cloned voices have no category — guard with a null so the line renders
  // only for preset voices that have an entry.
  const voiceCategory = (v: VoiceOption) =>
    v.isCloned ? null : ((t.talkingAvatar.voiceCategories as Record<string, string>)[v.id] ?? null);
  const languageLabel = (lang: VoiceOption["language"]) =>
    lang === "en"
      ? t.talkingAvatar.voiceLanguageEn
      : lang === "he"
        ? t.talkingAvatar.voiceLanguageHe
        : t.talkingAvatar.voiceLanguageMulti;
  const genderLabel = (g: VoiceOption["gender"]) =>
    g === "male" ? t.talkingAvatar.voiceGenderMale : t.talkingAvatar.voiceGenderFemale;

  const userClone = voices.find((v) => v.isCloned);
  const presetVoices = voices.filter((v) => !v.isCloned);
  const filteredVoices = activeCategory
    ? presetVoices.filter((v) => VOICE_CATEGORY_BY_ID[v.id] === activeCategory)
    : presetVoices;

  // Recompute the scroll affordance whenever the visible set changes (filter
  // switch shrinks/grows the row) or loading finishes.
  useEffect(() => {
    updateCanScroll();
  }, [updateCanScroll, filteredVoices.length, isLoading]);

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.stepPrefix.replace("{n}", String(stepNumber))}
          {t.talkingAvatar.voicePickerTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.voicePickerHint}
        </p>
      </div>

      {/* Existing clone row — kept ABOVE the preset grid because once cloned
          the clone IS the user's primary voice, and burying it below the
          presets would force them to scroll past every preset to find it. */}
      {!isLoading && userClone && (
        <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-3 md:p-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => playPreview(userClone)}
              className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center"
              aria-label={t.talkingAvatar.previewPlayAria}
            >
              {playingId === userClone.id ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ms-0.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => onSelectVoice(userClone.id)}
              aria-pressed={selectedVoiceId === userClone.id}
              className={cn(
                "flex-1 text-start rounded-lg px-3 py-2 transition-colors",
                selectedVoiceId === userClone.id
                  ? "bg-primary/10 ring-2 ring-primary/40"
                  : "hover:bg-card",
              )}
            >
              <p className="text-sm font-semibold text-foreground">
                {userClone.nameKey} · {t.talkingAvatar.clonedVoiceBadge}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {genderLabel(userClone.gender)} · {t.talkingAvatar.voiceLanguageHe}
              </p>
            </button>
            <button
              type="button"
              onClick={onDeleteClonedVoice}
              className="w-9 h-9 rounded-full border border-border bg-background hover:bg-destructive/10 text-destructive flex items-center justify-center"
              aria-label={t.talkingAvatar.clonedVoiceDelete}
              title={t.talkingAvatar.clonedVoiceDelete}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Category filter — "Categories" toggle reveals the buttons below it. */}
      {!isLoading && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setFilterOpen((o) => !o)}
            aria-expanded={filterOpen}
            className={cn(
              "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-sm font-medium transition-colors",
              activeCategory
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-card text-foreground hover:border-primary/40",
            )}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>{t.talkingAvatar.voiceFilterToggle}</span>
            {activeCategory && (
              <span className="text-xs opacity-80">· {categoryName(activeCategory)}</span>
            )}
            <ChevronDown
              className={cn("w-4 h-4 transition-transform duration-200", filterOpen && "rotate-180")}
            />
          </button>

          {filterOpen && (
            <div className="flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
              {([null, ...VOICE_CATEGORY_ORDER] as (VoiceCategoryKey | null)[]).map((c) => {
                const active = activeCategory === c;
                return (
                  <button
                    key={c ?? "all"}
                    type="button"
                    onClick={() => setActiveCategory(c)}
                    aria-pressed={active}
                    className={cn(
                      "px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all",
                      active
                        ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary text-primary ring-1 ring-primary/30"
                        : "bg-card border-border text-foreground hover:border-primary/40",
                    )}
                  >
                    {c === null ? t.talkingAvatar.voiceFilterAll : categoryName(c)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Preset voices grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          <span className="ml-2 text-sm text-muted-foreground">
            {t.talkingAvatar.voicesLoading}
          </span>
        </div>
      ) : (
        <div className="relative">
        <div
          ref={scrollRef}
          onScroll={updateCanScroll}
          className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {filteredVoices.map((v) => {
            const isActive = selectedVoiceId === v.id;
            const isPlaying = playingId === v.id;
            const category = voiceCategory(v);
            return (
              <div
                key={v.id}
                className={cn(
                  "relative flex flex-col gap-1 p-3 rounded-xl border transition-all duration-200 flex-shrink-0 w-[44vw] md:w-auto snap-start",
                  isActive
                    ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
                    : "bg-card border-border hover:border-primary/40 hover:shadow-sm",
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelectVoice(v.id)}
                  aria-pressed={isActive}
                  className="flex flex-col items-start gap-1 text-start"
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center mb-1 transition-colors",
                      isActive ? "bg-primary text-white" : "bg-primary/10 text-primary",
                    )}
                  >
                    <Mic className="w-4 h-4" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">{voiceName(v)}</p>
                  {category && (
                    <p className="text-[11px] text-muted-foreground">{category}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    {genderLabel(v.gender)} · {languageLabel(v.language)}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    playPreview(v);
                  }}
                  className={cn(
                    "absolute top-2 end-2 w-7 h-7 rounded-full flex items-center justify-center transition-colors",
                    isPlaying
                      ? "bg-primary text-white"
                      : "bg-background/90 text-foreground hover:bg-primary hover:text-white",
                  )}
                  aria-label={t.talkingAvatar.previewPlayAria}
                  title={t.talkingAvatar.previewPlayAria}
                >
                  {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ms-0.5" />}
                </button>
              </div>
            );
          })}
        </div>

          {/* Mobile-only "more voices" affordance — points toward the
              off-screen cards and scrolls one page; native swipe still works.
              Hidden on desktop (3-col grid, nothing to scroll). */}
          {canScrollMore && (
            <button
              type="button"
              onClick={scrollForward}
              aria-label={t.talkingAvatar.voiceMoreAria}
              className="md:hidden absolute top-1/2 -translate-y-1/2 end-1 z-10 w-9 h-9 rounded-full bg-background/90 backdrop-blur border border-border shadow-md flex items-center justify-center text-foreground"
            >
              {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
          )}
        </div>
      )}

      {/* Clone / upload CTAs — placed AFTER the preset grid so they read as
          "extras / advanced", not the main path. Two cards side-by-side
          (record + upload) with matching white styling so neither looks
          pre-selected. Free users are routed to the pricing modal upstream
          by the hook regardless of which CTA they clicked. */}
      {SHOW_CLONE_CTAS && !isLoading && isAuthenticated && !userClone && (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onOpenCloneModal("record")}
            className="flex flex-col items-start gap-1 p-3 rounded-xl bg-card border border-border hover:border-primary/40 hover:shadow-sm transition-all text-start"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1">
              <Sparkles className="w-4 h-4" />
            </div>
            <p className="text-sm font-semibold text-foreground">
              {t.talkingAvatar.cloneVoiceCta}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {t.talkingAvatar.cloneVoiceHint}
            </p>
          </button>

          <button
            type="button"
            onClick={() => onOpenCloneModal("upload")}
            className="flex flex-col items-start gap-1 p-3 rounded-xl bg-card border border-border hover:border-primary/40 hover:shadow-sm transition-all text-start"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1">
              <UploadCloud className="w-4 h-4" />
            </div>
            <p className="text-sm font-semibold text-foreground">
              {t.talkingAvatar.uploadVoiceCta}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {t.talkingAvatar.uploadVoiceHint}
            </p>
          </button>
        </div>
      )}

      {/* Tier picker hidden by product decision — Pro is gone, and surfacing
          a single Standard card with no alternative was just noise. The
          `tier` state still defaults to "standard" in the hook, so the
          render request keeps charging the same row as before; this is a
          UI-only change. Re-add the section if multi-tier returns. */}
    </div>
  );
}

